import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';

// Define color mappings from CSS variables to class names
const COLOR_CLASS_MAP: Record<string, string> = {
    'var(--mt-accent-bold-blue)': 'text-accent-bold-blue',
    'var(--mt-accent-bold-teal)': 'text-accent-bold-teal',
    'var(--mt-accent-bold-green)': 'text-accent-bold-green',
    'var(--mt-accent-bold-orange)': 'text-accent-bold-orange',
    'var(--mt-accent-bold-red)': 'text-accent-bold-red',
    'var(--mt-accent-bold-purple)': 'text-accent-bold-purple',
    'var(--mt-accent-gray)': 'text-accent-gray',
    'var(--mt-accent-blue)': 'text-accent-blue',
    'var(--mt-accent-teal)': 'text-accent-teal',
    'var(--mt-accent-green)': 'text-accent-green',
    'var(--mt-accent-orange)': 'text-accent-orange',
    'var(--mt-accent-red)': 'text-accent-red',
    'var(--mt-accent-purple)': 'text-accent-purple',
    'var(--mt-accent-blue-subtler)': 'text-accent-blue-subtler',
    'var(--mt-accent-teal-subtler)': 'text-accent-teal-subtler',
    'var(--mt-accent-green-subtler)': 'text-accent-green-subtler',
    'var(--mt-accent-yellow-subtler)': 'text-accent-yellow-subtler',
    'var(--mt-accent-red-subtler)': 'text-accent-red-subtler',
    'var(--mt-accent-purple-subtler)': 'text-accent-purple-subtler',
    'hsl(var(--foreground))': 'text-foreground',
    'hsl(var(--background))': 'text-background',
};

export interface ColorOptions {
    types: string[];
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        color: {
            setColor: (color: string) => ReturnType;
            unsetColor: () => ReturnType;
        };
    }
}

export const Color = Mark.create<ColorOptions>({
    name: 'textColor',

    addOptions() {
        return {
            types: ['textStyle'],
        };
    },

    addAttributes() {
        return {
            color: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-color'),
                renderHTML: (attributes) => {
                    if (!attributes.color) {
                        return {};
                    }

                    const className = COLOR_CLASS_MAP[attributes.color];
                    if (className) {
                        return {
                            'data-color': attributes.color,
                            class: className,
                        };
                    }

                    return {
                        'data-color': attributes.color,
                    };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-color]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes(HTMLAttributes), 0];
    },

    addCommands() {
        return {
            setColor:
                (color: string) =>
                ({ commands }) => {
                    return commands.setMark(this.name, { color });
                },
            unsetColor:
                () =>
                ({ commands }) => {
                    return commands.unsetMark(this.name);
                },
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                props: {
                    handleKeyDown: (_, event) => {
                        if (event.key === 'Enter') {
                            this.editor.commands.unsetColor();
                        }
                        return false;
                    },
                },
            }),
        ];
    },
});
